import {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {
  AlertCircle,
  CheckCircle2,
  FileText,
  Loader2,
  Plus,
  Sparkles,
  X,
} from 'lucide-react';
import {cn} from '@/matic/lib/utils/cn';
import {useNavigateToLibraryRecord} from '@/matic/features/_shared/hooks/useNavigateToLibraryRecord';
import {useFile} from '../hooks/useFile';
import {useFileTags} from '../hooks/useFileTags';
import {useFileMutations} from '../hooks/useFileMutations';
import {AssociationChip} from '@/matic/features/relay/components/shared/AssociationChip';
import {AssociationPicker} from '@/matic/features/relay/components/shared/AssociationPicker';
import type {QuickDropItem} from '../hooks/useQuickDropUpload';
import type {FileCategory, ManagedFile} from '../types/files-types';

type Props = {
  items: QuickDropItem[];
  onView: (file: ManagedFile) => void;
  onDismiss: (id: string) => void;
  onClear: () => void;
};

function statusIcon(item: QuickDropItem) {
  switch (item.status) {
    case 'uploading': {
      return <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-400" />;
    }

    case 'classifying': {
      return (
        <Sparkles className="h-3.5 w-3.5 animate-pulse text-primary-500" />
      );
    }

    case 'done': {
      return <CheckCircle2 className="h-3.5 w-3.5 text-success-600" />;
    }

    case 'error': {
      return <AlertCircle className="h-3.5 w-3.5 text-error-600" />;
    }
  }
}

function statusLine(item: QuickDropItem): string {
  switch (item.status) {
    case 'uploading': {
      return 'Uploading…';
    }

    case 'classifying': {
      return 'Classifying…';
    }

    case 'error': {
      return item.error ?? 'Upload failed';
    }

    case 'done': {
      return '';
    }
  }
}

/** Category pill — click it to open a compact dropdown of every category,
 *  portal'd to the body so it always escapes the tray's own scroll clipping
 *  regardless of where the row sits in the list. Opens upward since the
 *  tray lives at the bottom of the screen. */
function CategoryPill({
  categoryId,
  categories,
  onChange,
}: {
  categoryId: string | null;
  categories: FileCategory[];
  onChange: (categoryId: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>();
  const current = categories.find((c) => c.id === categoryId);

  useEffect(() => {
    if (!open) return;
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = 160;
    const margin = 8;
    setStyle({
      position: 'fixed',
      left: Math.min(rect.left, window.innerWidth - width - margin),
      bottom: window.innerHeight - rect.top + margin,
      width,
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };

    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          setOpen((o) => !o);
        }}
        className={cn(
          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors',
          current
            ? 'bg-primary-50 text-primary-700 hover:bg-primary-100'
            : 'border border-dashed border-neutral-300 text-neutral-400 hover:border-neutral-400 hover:text-neutral-500'
        )}
      >
        <Sparkles className="h-2.5 w-2.5 shrink-0" />
        <span className="max-w-[6rem] truncate">
          {current ? current.name : 'Tag'}
        </span>
      </button>
      {open &&
        style &&
        createPortal(
          <div
            ref={menuRef}
            style={style}
            className="z-50 max-h-48 overflow-y-auto rounded-md border border-neutral-200 bg-white py-1 shadow-lg"
          >
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
              className="block w-full px-3 py-1.5 text-left text-xs text-neutral-500 hover:bg-neutral-50"
            >
              No category
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                type="button"
                onClick={() => {
                  onChange(category.id);
                  setOpen(false);
                }}
                className={cn(
                  'block w-full truncate px-3 py-1.5 text-left text-xs hover:bg-neutral-50',
                  category.id === categoryId
                    ? 'font-medium text-primary-700'
                    : 'text-neutral-700'
                )}
              >
                {category.name}
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
}

/** One filed item's editable tag row — category pill first, then each
 *  linked record as a removable pill, then a "+ Link" affordance that swaps
 *  in the full `AssociationPicker` search box right there in the row.
 *  Reads live via `useFile` so a pill always reflects the latest state,
 *  including edits made a moment ago from this same row. */
function FileTagsRow({fileId}: {fileId: string}) {
  const {data: liveFile} = useFile(fileId);
  const {data: categories} = useFileTags();
  const mutations = useFileMutations();
  const navigateToRecord = useNavigateToLibraryRecord();
  const [linking, setLinking] = useState(false);

  if (!liveFile) return null;

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
      <CategoryPill
        categoryId={liveFile.categoryId}
        categories={categories ?? []}
        onChange={(categoryId) => {
          mutations.updateFile.mutate({id: fileId, input: {categoryId}});
        }}
      />
      {liveFile.associations.map((assoc) => (
        <AssociationChip
          key={assoc.id}
          assoc={assoc}
          includeTypeName
          onNavigate={navigateToRecord}
          onRemove={(assocId) => {
            mutations.removeAssociation.mutate({fileId, assocId});
          }}
        />
      ))}
      {linking ? (
        <AssociationPicker
          allowCategorySelection
          autoOpen
          placeholder="Link to a record…"
          className="w-56 flex-none"
          onAdd={(selection) => {
            mutations.addAssociation.mutate({
              fileId,
              objectTypeId: selection.objectTypeId,
              objectRecordId: selection.objectRecordId,
            });
            setLinking(false);
          }}
          onClose={() => {
            setLinking(false);
          }}
        />
      ) : (
        <button
          type="button"
          onClick={() => {
            setLinking(true);
          }}
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-neutral-300 px-2 py-0.5 text-[11px] font-medium text-neutral-400 transition-colors hover:border-neutral-400 hover:text-neutral-500"
        >
          <Plus className="h-2.5 w-2.5 shrink-0" />
          Link
        </button>
      )}
    </div>
  );
}

/**
 * The quick-drop filing tray — a compact, non-blocking bottom-right panel
 * showing each dropped file's journey: uploading → classifying → filed,
 * with its category and linked records rendered as editable pills right on
 * the card. No modal detour to fix a wrong or missing classification — click
 * the pill, pick again, right there above the "+" button.
 */
export function QuickDropTray({items, onView, onDismiss, onClear}: Props) {
  if (items.length === 0) return null;

  const settled = items.every(
    (item) => item.status === 'done' || item.status === 'error'
  );
  const doneCount = items.filter((item) => item.status === 'done').length;

  return (
    // Bottom-24: clears the FAB (h-14 at bottom-6, i.e. the bottom ~80px of
    // the viewport) so the tray's own "View"/dismiss actions are never
    // obstructed behind the button.
    <div className="fixed bottom-24 right-4 z-40 w-96 overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-lg">
      <div className="flex items-center justify-between border-b border-neutral-100 px-3 py-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
          {settled
            ? `Added ${doneCount} file${doneCount === 1 ? '' : 's'}`
            : 'Adding files…'}
        </p>
        {settled && (
          <button
            type="button"
            className="rounded p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
            aria-label="Dismiss all"
            onClick={onClear}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <ul className="max-h-96 divide-y divide-neutral-100 overflow-y-auto">
        {items.map((item) => (
          <li key={item.id} className="px-3 py-2.5">
            <div className="flex items-start gap-2.5">
              <span className="mt-0.5 shrink-0">{statusIcon(item)}</span>
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1.5 truncate text-sm font-medium text-neutral-900">
                  <FileText className="h-3 w-3 shrink-0 text-neutral-300" />
                  <span className="truncate" title={item.filename}>
                    {item.file?.name ?? item.filename}
                  </span>
                </p>
                {statusLine(item) && (
                  <p
                    className={cn(
                      'mt-0.5 text-xs',
                      item.status === 'error'
                        ? 'text-error-600'
                        : 'text-neutral-500'
                    )}
                  >
                    {statusLine(item)}
                  </p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {item.status === 'done' && item.file && (
                  <button
                    type="button"
                    className="rounded px-1.5 py-0.5 text-xs font-medium text-primary-600 transition-colors hover:bg-primary-50"
                    onClick={() => {
                      onView(item.file!);
                    }}
                  >
                    View
                  </button>
                )}
                {(item.status === 'done' || item.status === 'error') && (
                  <button
                    type="button"
                    aria-label="Dismiss"
                    className="rounded p-1 text-neutral-300 transition-colors hover:bg-neutral-100 hover:text-neutral-500"
                    onClick={() => {
                      onDismiss(item.id);
                    }}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
            {item.status === 'done' && item.file && (
              <FileTagsRow fileId={item.file.id} />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
